import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useProjectListActions, useProjectListState } from '../../shared/hooks/useApi/useProjectList';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { projectBuilder } from '../../shared/converter/entityBuilder';
import { DeviceType, ModalType, ProjectListType } from '../../shared/enum/EnumRepository';
import { useModalStore } from '../../shared/store/portal/modalStore';
import reorderIcon from '../../asset/icon/reorder.png';
import projectCardIcon from '../../asset/icon/projectCard.png';
import projectTicketIcon from '../../asset/icon/projectTicket.png';
import projectStackedIcon from '../../asset/icon/projectStacked.png';
import createIcon from '../../asset/icon/create.png';
import HintWrapper from '../../shared/component/headless/button/HintWrapper';
import { getHintMessage } from '../../shared/constants/hintMessages';
import { getPageMessage } from '../../shared/constants/pageMessages';
import { useIsPageOwner } from '../../shared/hooks/useIsPageOwner';
import { useDeviceStore } from '../../shared/store/deviceStore';
import ProjectSimpleMobile from './mobile/ProjectSimpleMobile';
import ProjectSimpleTicket from './ProjectSimpleTicket';
import ProjectSimpleCard from './ProjectSimpleCard';
import ProjectSimpleStacked from './ProjectSimpleStacked';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { ProjectSimpleData } from '../../shared/dto/EntityRepository';
import { usePageOwnerIdentityStore } from '../../shared/store/pageOwnerIdentityStore';
import { useParams } from 'react-router-dom';
import { isPageOwnerMatchedToRaw } from '../../shared/util/pageOwnerMatch';
import { canShowProjectList } from '../../shared/util/visibility/projectListVisibility';
import type { ResourceScope } from '../../shared/util/scopedResource';

const PROJECT_LIST_TYPE_ORDER: ProjectListType[] = [
  ProjectListType.CARD,
  ProjectListType.TICKET,
  ProjectListType.STACKED,
];

const PROJECT_LIST_TYPE_ICON: Record<ProjectListType, string> = {
  [ProjectListType.CARD]: projectCardIcon,
  [ProjectListType.TICKET]: projectTicketIcon,
  [ProjectListType.STACKED]: projectStackedIcon,
};

const getNextProjectListType = (current: ProjectListType): ProjectListType => {
  const index = PROJECT_LIST_TYPE_ORDER.indexOf(current);
  const nextIndex = (index + 1) % PROJECT_LIST_TYPE_ORDER.length;
  return PROJECT_LIST_TYPE_ORDER[nextIndex];
};

const renderDesktopProjectItem = (type: ProjectListType, data: ProjectSimpleData) => {
  switch (type) {
    case ProjectListType.TICKET:
      return <ProjectSimpleTicket key={data.id} data={data} />;
      case ProjectListType.STACKED:
        return <ProjectSimpleStacked key={data.id} data={data} />;
        case ProjectListType.CARD:
          default:
      return <ProjectSimpleCard key={data.id} data={data} />;
  }
};

const ProjectList: React.FC = () => {
  const { deviceType } = useDeviceStore();
  const isOwner = useIsPageOwner();
  const { isEditMode, setEditMode } = useEditMode();
  const { projects, projectListType, listOwnerAui, listScope, status } = useProjectListState();
  const {
    getProjectList, 
    getAllProjectsForOwner, 
    createProject, 
    updateProjectListType
  } = useProjectListActions();
  const setStandardModal = useModalStore((state) => state.setStandardModal);
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const { HANDLE: raw } = useParams<{ HANDLE?: string }>();
  const currentScope: ResourceScope = isOwner ? 'owner' : 'public';
  const canShowList = canShowProjectList({
    pageOwnerAui: pageOwner?.aui,
    listOwnerAui,
    listScope,
    currentScope,
    status,
  });
  const visibleProjects = canShowList ? projects : [];
  
  useEffect(() => {
    const getProjectListTemp = async () => {
      if (!isPageOwnerMatchedToRaw(raw, pageOwner)) return;
      if (isOwner) {
        await getAllProjectsForOwner();
      } else {
        await getProjectList();
      }
    }
    getProjectListTemp();
  }, [raw, pageOwner?.aui, isOwner]);

  const handleCreate = async () => {
    const newIndex = projects.length;
    const newTitle = 'Project_' + (newIndex + 1);
    try {
      await createProject(projectBuilder(newTitle, newIndex));
    } catch (error) {
    } finally {
      setEditMode(false);
    }
  };

  const handleReOrder = () => {
    setStandardModal({
      modalType: ModalType.INDEXING,
      title: "Project",
      value: null,
      handleChange: () => { },
    });
  }

  const handleChangeProjectView = async () => {
    const currentType = projectListType ?? ProjectListType.CARD;
    await updateProjectListType({
      projectListType: getNextProjectListType(currentType),
    });
  };

  const isMobile = deviceType === DeviceType.MOBILE;
  const currentProjectListType = projectListType ?? ProjectListType.CARD;
  const nextProjectListType = getNextProjectListType(currentProjectListType);

  // const handleSelectTag = (tag: TagData) => {
  //   if (selectedTags.some((t) => t.id === tag.id)) {
  //     removeSelectedTag(tag);
  //   } else {
  //     addSelectedTag(tag);
  //   }
  // };
  return (
    <>
    {/* <ProjectTagStation $isMobile={isMobile}>
    <ProjectTagFactorContainer>
        {tags.map((tag) => (
          <ProjectTagFactor 
          key={tag.id} 
          title={tag.name} 
          $isSelected={selectedTags.some((t) => t.id === tag.id)}
          onClick={() => handleSelectTag(tag)}
          >
            {tag.name}
          </ProjectTagFactor>
        ))}
      </ProjectTagFactorContainer>
    </ProjectTagStation> */}
    <ProjectSimpleList>
      {visibleProjects.map((each) => (
        isMobile
          ? <ProjectSimpleMobile key={each.id} data={each} />
          : renderDesktopProjectItem(currentProjectListType, each)
      ))}
      {canShowList && visibleProjects.length === 0 &&
        <EmptyContainer>
          {getPageMessage('NO_PROJECTS_FOUND')}
        </EmptyContainer>
      }
      {isEditMode &&
        <BtnContainer>
          <HintWrapper content={getHintMessage('CREATE_PROJECT')}>
            <UniversalIconBtn 
              size="standard" 
              border={true} 
              backgroundImg={createIcon} 
              onClick={handleCreate} />
          </HintWrapper>
          {!isMobile &&          
            <HintWrapper content={getHintMessage('CHANGE_PROJECT_VIEW')}>
              <UniversalIconBtn 
                size="standard" 
                border={true} 
                backgroundImg={PROJECT_LIST_TYPE_ICON[nextProjectListType]}
                onClick={handleChangeProjectView} />
            </HintWrapper>
          }
          <HintWrapper content={getHintMessage('REORDER_PROJECTS')}>
            <UniversalIconBtn 
                size="standard" 
                border={true} 
                backgroundImg={reorderIcon} 
                onClick={handleReOrder} />
          </HintWrapper>
        </BtnContainer>
      }
    </ProjectSimpleList>
    </>
  );
}

const ProjectTagStation = styled.section<{ $isMobile: boolean }>`
  margin-top: ${({ $isMobile }) => $isMobile ? 'clamp(44px, 6vh, 100px)' : 'clamp(30px, 4vh, 30px)'};
  padding: 0 ${({ $isMobile }) => $isMobile ? 'clamp(18px, 5vw, 24px)' : 'calc(8vw)'};
  margin-bottom: ${({ $isMobile }) => $isMobile ? '0' : '1vh'};
`;

const ProjectTagFactorContainer = styled.div`
  width: fit-content;
  max-width: 100%;
  min-width: 0;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 12px;
`;

const ProjectTagFactor = styled.span <{ $isSelected: boolean }>`
  width: fit-content;
  flex-shrink: 0;
  border-bottom: 1px dotted ${({ $isSelected, theme }) => $isSelected ? theme.colors.color_Gray_03 : "transparent"};
  color: ${({ $isSelected, theme }) => $isSelected ? theme.colors.color_Gray_03 : theme.colors.color_Gray_05};
  opacity: ${({ $isSelected }) => $isSelected ? 1 : 0.4};
  ${({ theme }) => theme.typography.Body_04};
  user-select: none;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
`;


const ProjectSimpleList = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  margin-top: clamp(40px, 8vh, 80px);
  margin-bottom: 6vh;
  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const EmptyContainer = styled.div`
  width: 100%;
  height: 90vh;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const BtnContainer = styled.div`
  position: fixed;
  bottom: 4vh;
  left: 50%;
  transform: translateX(-50%);
  width: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5vw;
  z-index: 4;
`

export default ProjectList;
